/**
 * Overlay chrome: the AI-assistant search dialog, the lead-magnet gate dialog and
 * the floating assistant button. All three come from the approved mock-up.
 *
 * Both dialogs are real modals: role="dialog" aria-modal, focus moved in on open
 * and restored on close, Tab cycles inside, Escape and the backdrop close them.
 */
import { clear, el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { MagnetSeed } from "@mb-group/content-seed";
import { contentSeed } from "../data/seed.js";
import { plainText } from "../data/typography.js";
import type { SearchHit } from "../data/types.js";
import { requestDownload, subscribeNewsletter, type LeadContext } from "../data/leads.js";
import { toast } from "./common.js";

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Wrap content in a modal shell and return it plus a focus-management handle. */
function dialog(
	ctx: AppContext,
	options: { labelledBy: string; onClose: () => void; className: string },
	body: HTMLElement,
): HTMLElement {
	const panel = el(
		"div",
		{
			class: `mb-dialog__panel ${options.className}`,
			role: "dialog",
			"aria-modal": "true",
			"aria-labelledby": options.labelledBy,
		},
		body,
	);

	const backdrop = el(
		"div",
		{
			class: "mb-dialog",
			onClick: (event: Event) => {
				if (event.target === event.currentTarget) options.onClose();
			},
		},
		panel,
	);

	backdrop.addEventListener("keydown", (event) => {
		const e = event;
		if (e.key === "Escape") {
			e.preventDefault();
			options.onClose();
			return;
		}
		if (e.key !== "Tab") return;
		const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
			(node) => node.offsetParent !== null || node === document.activeElement,
		);
		if (focusable.length === 0) return;
		const first = focusable[0]!;
		const last = focusable[focusable.length - 1]!;
		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	});

	// Move focus to the first control once the dialog is in the document.
	window.requestAnimationFrame(() => {
		const target = panel.querySelector<HTMLElement>(FOCUSABLE);
		target?.focus();
	});

	void ctx; // ctx kept for symmetry with the other component factories
	return backdrop;
}

function closeButton(label: string, onClose: () => void, variant: "circle" | "plain"): HTMLElement {
	return el(
		"button",
		{
			class: `mb-dialog__close mb-dialog__close--${variant}`,
			type: "button",
			"aria-label": label,
			onClick: onClose,
		},
		"×",
	);
}

// --- assistant ---------------------------------------------------------------

/**
 * The assistant overlay. It is the site search behind a conversational frame:
 * the query goes to the gateway's search (which falls back to a local DEMO
 * search), and the "recommendation" summarises the top hit.
 */
export function assistantDialog(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	const close = () => ctx.store.set({ assistantOpen: false });

	const input = el("input", {
		class: "mb-assistant__input",
		type: "search",
		value: ctx.store.get().assistantQuery,
		placeholder: copy.assistant.placeholder,
		"aria-label": copy.assistant.subtitle,
		autocomplete: "off",
	});

	const answer = el("div", { class: "mb-assistant__answer", hidden: true });
	const results = el("ul", { class: "mb-assistant__results" });
	const resultsRegion = el(
		"div",
		{
			class: "mb-assistant__resultswrap",
			role: "region",
			"aria-live": "polite",
			"aria-label": copy.assistant.resultsLabel,
		},
		results,
	);

	let token = 0;

	async function run(input: string): Promise<void> {
		const current = ++token;
		// Prompt chips are typeset copy: state and analytics get plain spaces.
		const query = plainText(input);
		const trimmed = query.trim();
		// Only write on a real change: an unconditional set() notifies subscribers
		// on every keystroke (and on the initial seeding run) for no benefit.
		if (ctx.store.get().assistantQuery !== query) ctx.store.set({ assistantQuery: query });

		const result = await ctx.gateway.search(trimmed);
		if (current !== token) return; // a newer keystroke already won

		clear(results);
		for (const hit of result.hits.slice(0, 12)) results.appendChild(hitRow(ctx, hit, close));

		if (trimmed.length > 2) {
			const top = result.hits[0];
			answer.hidden = false;
			clear(answer);
			answer.appendChild(el("p", { class: "mb-eyebrow" }, copy.assistant.recommendation));
			answer.appendChild(
				el(
					"p",
					{ class: "mb-assistant__answertext" },
					top
						? copy.assistant.answerFound
								.replace("{service}", top.title)
								.replace("{category}", hitTypeLabel(ctx, top))
						: copy.assistant.answerEmpty,
				),
			);
			ctx.track({ type: "search", query: trimmed, results: result.total });
		} else {
			answer.hidden = true;
		}
	}

	let debounce = 0;
	input.addEventListener("input", () => {
		window.clearTimeout(debounce);
		const value = input.value;
		debounce = window.setTimeout(() => void run(value), 250);
	});
	input.addEventListener("keydown", (event) => {
		if (event.key !== "Enter") return;
		event.preventDefault();
		window.clearTimeout(debounce);
		void run(input.value);
	});

	const prompts = el(
		"div",
		{ class: "mb-assistant__prompts" },
		el("span", { class: "mb-assistant__promptslabel" }, copy.assistant.frequent),
		...copy.assistant.prompts.map((prompt) =>
			el(
				"button",
				{
					class: "mb-assistant__prompt",
					type: "button",
					onClick: () => {
						input.value = prompt;
						void run(prompt);
						input.focus();
					},
				},
				prompt,
			),
		),
	);

	const body = el(
		"div",
		{ class: "mb-assistant" },
		el(
			"div",
			{ class: "mb-assistant__head" },
			el(
				"div",
				{ class: "mb-assistant__identity" },
				el("span", { class: "mb-assistant__pulse", "aria-hidden": "true" }),
				el(
					"div",
					null,
					el(
						"h2",
						{ class: "mb-assistant__title", id: "mb-assistant-title" },
						copy.assistant.title,
					),
					el("p", { class: "mb-assistant__subtitle" }, copy.assistant.subtitle),
				),
			),
			closeButton(copy.nav.close, close, "circle"),
		),
		el(
			"div",
			{ class: "mb-assistant__searchrow" },
			el("span", { class: "mb-assistant__glass", "aria-hidden": "true" }),
			input,
		),
		prompts,
		answer,
		resultsRegion,
		el(
			"div",
			{ class: "mb-assistant__foot" },
			el("p", { class: "mb-assistant__note" }, copy.assistant.privacyNote),
			el(
				"a",
				{
					class: "mb-cta mb-cta--solid",
					href: ctx.href("contact"),
					onClick: () => {
						ctx.track({ type: "cta_click", cta: "assistant_expert" });
						close();
					},
				},
				copy.assistant.expertCta,
			),
		),
	);

	// Populate with the default (empty-query) result set.
	void run(input.value);

	return dialog(
		ctx,
		{ labelledBy: "mb-assistant-title", onClose: close, className: "mb-dialog__panel--wide" },
		body,
	);
}

function hitTypeLabel(ctx: AppContext, hit: SearchHit): string {
	const isEn = ctx.locale() === "en";
	switch (hit.contentType) {
		case "service":
			return isEn ? "Services" : "Usługi";
		case "case_study":
			return isEn ? "Case studies" : "Realizacje";
		case "article":
			return isEn ? "Insights" : "Wiedza";
		case "company":
			return isEn ? "Group" : "Grupa";
		default:
			return isEn ? "Content" : "Treści";
	}
}

function hitRow(ctx: AppContext, hit: SearchHit, close: () => void): HTMLElement {
	return el(
		"li",
		{ class: "mb-assistant__result" },
		el(
			"a",
			{ class: "mb-assistant__resultlink", href: hit.url, onClick: close },
			el(
				"span",
				null,
				el("span", { class: "mb-assistant__resulttitle" }, hit.title),
				el("span", { class: "mb-assistant__resultmeta" }, hitTypeLabel(ctx, hit)),
			),
			el("span", { class: "mb-assistant__resultarrow", "aria-hidden": "true" }, "→"),
		),
	);
}

// --- lead-magnet gate --------------------------------------------------------

interface GateItem {
	kind: string;
	tag: string;
	title: string;
	desc: string;
	cta: string;
	doneTitle: string;
	doneDesc: string;
	/** Resource slug for the download mutation; absent for the newsletter. */
	slug?: string;
}

function gateItemFor(ctx: AppContext): GateItem | null {
	const target = ctx.store.get().gate;
	if (target === null) return null;
	const copy = ctx.copy();
	if (target === "newsletter") {
		return {
			kind: copy.newsletter.kind,
			tag: copy.newsletter.tag,
			title: copy.newsletter.title,
			desc: copy.newsletter.desc,
			cta: copy.newsletter.submit,
			doneTitle: copy.newsletter.doneTitle,
			doneDesc: copy.newsletter.doneDesc,
		};
	}
	const magnet: MagnetSeed | undefined = contentSeed(ctx.locale()).magnets[target];
	if (!magnet) return null;
	return {
		kind: magnet.kind,
		tag: magnet.tag,
		title: magnet.title,
		desc: magnet.desc,
		cta: magnet.cta,
		doneTitle: magnet.doneTitle,
		doneDesc: magnet.doneDesc,
		slug: magnet.slug,
	};
}

export function gateDialog(ctx: AppContext): HTMLElement | null {
	const resolved = gateItemFor(ctx);
	if (!resolved) return null;
	// Bound to a non-nullable local: `send()` below is a hoisted declaration, and
	// TypeScript does not carry the null-narrowing of `resolved` into it.
	const item: GateItem = resolved;

	const copy = ctx.copy();
	const close = () => ctx.store.set({ gate: null, gateSent: false });
	const leadCtx: LeadContext = { client: ctx.client, site: ctx.site, locale: ctx.locale() };

	if (ctx.store.get().gateSent) {
		const body = el(
			"div",
			{ class: "mb-gate mb-gate--done" },
			el("span", { class: "mb-gate__tick", "aria-hidden": "true" }, "✓"),
			el("h2", { class: "mb-gate__title", id: "mb-gate-title" }, item.doneTitle),
			el("p", { class: "mb-gate__desc" }, item.doneDesc),
			el(
				"button",
				{ class: "mb-cta mb-cta--solid", type: "button", onClick: close },
				copy.nav.close,
			),
		);
		return dialog(
			ctx,
			{ labelledBy: "mb-gate-title", onClose: close, className: "mb-dialog__panel--narrow" },
			body,
		);
	}

	const email = el("input", {
		class: "mb-field__input",
		type: "email",
		name: "email",
		required: true,
		placeholder: copy.gate.email,
		"aria-label": copy.gate.email,
		autocomplete: "email",
	});

	const company = el("input", {
		class: "mb-field__input",
		type: "text",
		name: "company",
		placeholder: copy.gate.company,
		"aria-label": copy.gate.company,
		autocomplete: "organization",
	});

	const error = el("p", { class: "mb-field__error", role: "alert", hidden: true });
	const submit = el(
		"button",
		{ class: "mb-cta mb-cta--accent mb-gate__submit", type: "submit" },
		item.cta,
	);

	const form = el(
		"form",
		{
			class: "mb-gate__form",
			novalidate: true,
			onSubmit: (event: Event) => {
				event.preventDefault();
				void send();
			},
		},
		email,
		company,
		submit,
		error,
		el("p", { class: "mb-gate__note" }, copy.gate.privacyNote),
	);

	async function send(): Promise<void> {
		error.hidden = true;
		const address = email.value.trim();
		if (!address || !address.includes("@")) {
			error.hidden = false;
			error.textContent = ctx.t("form.invalidEmail");
			email.focus();
			return;
		}
		submit.setAttribute("disabled", "");
		submit.textContent = ctx.t("form.submitting");

		const outcome = item.slug
			? await requestDownload(leadCtx, item.slug, address, company.value.trim() || undefined)
			: await subscribeNewsletter(leadCtx, address);

		submit.removeAttribute("disabled");
		submit.textContent = item.cta;

		if (outcome.ok) {
			ctx.track({ type: "lead_submit", form: item.slug ?? "newsletter" });
			ctx.store.set({ gateSent: true });
			return;
		}
		error.hidden = false;
		error.textContent = outcome.unavailable
			? ctx.t("form.genericError")
			: (outcome.message ?? ctx.t("form.genericError"));
	}

	const body = el(
		"div",
		{ class: "mb-gate" },
		el(
			"div",
			{ class: "mb-gate__head" },
			el("p", { class: "mb-eyebrow" }, item.kind),
			closeButton(copy.gate.close, close, "plain"),
		),
		el("h2", { class: "mb-gate__title", id: "mb-gate-title" }, item.title),
		el("p", { class: "mb-gate__desc" }, item.desc),
		form,
	);

	return dialog(
		ctx,
		{ labelledBy: "mb-gate-title", onClose: close, className: "mb-dialog__panel--narrow" },
		body,
	);
}

// --- floating assistant button ----------------------------------------------

export function stickyAssistant(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	return el(
		"div",
		{ class: "mb-sticky" },
		el(
			"button",
			{
				class: "mb-sticky__button",
				type: "button",
				onClick: () => {
					ctx.track({ type: "cta_click", cta: "sticky_assistant" });
					ctx.openAssistant();
				},
			},
			el("span", { class: "mb-sticky__pulse", "aria-hidden": "true" }),
			copy.assistant.openLabel,
		),
	);
}

/** Inline newsletter strip used on the home and knowledge pages. */
export function newsletterStrip(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	const leadCtx: LeadContext = { client: ctx.client, site: ctx.site, locale: ctx.locale() };

	const input = el("input", {
		class: "mb-field__input",
		type: "email",
		placeholder: copy.newsletter.emailPlaceholder,
		"aria-label": copy.newsletter.emailPlaceholder,
		autocomplete: "email",
	});

	const button = el(
		"button",
		{ class: "mb-cta mb-cta--accent", type: "submit" },
		copy.newsletter.submit,
	);

	const form = el(
		"form",
		{
			class: "mb-newsletter__form",
			novalidate: true,
			onSubmit: (event: Event) => {
				event.preventDefault();
				void send();
			},
		},
		input,
		button,
	);

	async function send(): Promise<void> {
		const address = input.value.trim();
		if (!address.includes("@")) {
			toast(ctx.t("form.invalidEmail"));
			input.focus();
			return;
		}
		button.setAttribute("disabled", "");
		const outcome = await subscribeNewsletter(leadCtx, address);
		button.removeAttribute("disabled");
		if (outcome.ok) {
			ctx.track({ type: "lead_submit", form: "newsletter" });
			input.value = "";
			ctx.store.set({ gate: "newsletter", gateSent: true });
			return;
		}
		toast(
			outcome.unavailable
				? ctx.t("form.genericError")
				: (outcome.message ?? ctx.t("form.genericError")),
		);
	}

	return el(
		"div",
		{ class: "mb-newsletter" },
		el(
			"div",
			{ class: "mb-newsletter__text" },
			el("h2", { class: "mb-newsletter__title" }, copy.newsletter.title),
			el("p", { class: "mb-newsletter__desc" }, copy.newsletter.desc),
		),
		form,
	);
}
